import React from "react";
import { Button, ButtonGroup, Col, Form, Row } from "react-bootstrap";
import { Controller } from "react-hook-form";
import Select from "react-select";

import { useRosterContext } from "../contexts/Roster";
import { PlayerFormProps } from "../types/player";

import { selectPositions } from "../../../../../features/global/globalSlice";
import { useAppSelector } from "../../../../../hooks/reduxHooks";
import { displayFormAlert } from "../../../../../lib/utils";

const PlayerForm = (props: PlayerFormProps): React.JSX.Element => {
    const {
        methods,
        isAddingPlayer,
        player,
        maxRosterSize,
        isPlayerActive,
        isPrimaryTeam,
        teamAtMaxSize,
    } = props;
    const { rosterCount } = useRosterContext();
    const positions = useAppSelector(selectPositions);

    return (
        <div data-testid="playerForm">
            <Row>
                <Col>
                    <h2>
                        {isAddingPlayer
                            ? "Adding Player"
                            : `Editing ${player?.firstName} ${player?.lastName}`}
                    </h2>
                    {maxRosterSize < rosterCount && (
                        <h3 className="text-danger">
                            This team is at the maximum active player count of{" "}
                            {maxRosterSize}. Please deactivate players to bring
                            your active player count down before
                            adding/activating new players.
                        </h3>
                    )}
                </Col>
            </Row>
            <Row>
                <Form.Control
                    type="hidden"
                    {...methods.register("active")}
                    id="active"
                />
                <Form.Control
                    type="hidden"
                    {...methods.register("playerId")}
                    id="playerId"
                />
                <Form.Group
                    controlId="active"
                    as={Col}
                    xs={6}
                    lg={5}
                    xl={2}
                    className="col"
                >
                    <Form.Label>Status</Form.Label>
                    <ButtonGroup>
                        <Button
                            variant={
                                isPlayerActive ? "primary" : "outline-primary"
                            }
                            onClick={() => methods.setValue("active", 1)}
                            disabled={
                                isPlayerActive == 0 &&
                                maxRosterSize < rosterCount
                            }
                        >
                            Active
                        </Button>
                        <Button
                            variant={
                                isPlayerActive ? "outline-primary" : "primary"
                            }
                            onClick={() => methods.setValue("active", 0)}
                        >
                            Inactive
                        </Button>
                    </ButtonGroup>
                </Form.Group>
                <Form.Group
                    controlId="isPrimary"
                    as={Col}
                    xs={6}
                    lg={5}
                    xl={2}
                    className="col"
                >
                    <Form.Label>Primary Team?</Form.Label>
                    <Form.Control
                        type="hidden"
                        {...methods.register("isPrimary")}
                        disabled={teamAtMaxSize}
                    />
                    <ButtonGroup>
                        <Button
                            variant={
                                isPrimaryTeam ? "primary" : "outline-primary"
                            }
                            onClick={() => methods.setValue("isPrimary", true)}
                            disabled={teamAtMaxSize}
                        >
                            Yes
                        </Button>
                        <Button
                            variant={
                                isPrimaryTeam ? "outline-primary" : "primary"
                            }
                            onClick={() => methods.setValue("isPrimary", false)}
                            disabled={teamAtMaxSize}
                        >
                            No
                        </Button>
                    </ButtonGroup>
                </Form.Group>
                <Form.Group
                    controlId="firstName"
                    as={Col}
                    xs={6}
                    lg={4}
                    className="col"
                >
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                        type="text"
                        {...methods.register("firstName")}
                        aria-invalid={
                            methods.formState.errors.firstName
                                ? "true"
                                : "false"
                        }
                        disabled={teamAtMaxSize}
                    />
                    {methods.formState.errors.firstName &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.firstName
                                .message as string,
                        })}
                </Form.Group>
                <Form.Group
                    controlId="lastName"
                    as={Col}
                    xs={6}
                    lg={4}
                    className="col"
                >
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                        type="text"
                        {...methods.register("lastName")}
                        aria-invalid={
                            methods.formState.errors.lastName ? "true" : "false"
                        }
                        disabled={teamAtMaxSize}
                    />
                    {methods.formState.errors.lastName &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.lastName
                                .message as string,
                        })}
                </Form.Group>
            </Row>
            <Row>
                <Form.Group
                    controlId="uniformNumber"
                    as={Col}
                    xs={6}
                    lg={4}
                    className="col"
                >
                    <Form.Label>Uniform #</Form.Label>
                    <Form.Control
                        type="text"
                        {...methods.register("uniformNumber")}
                        aria-invalid={
                            methods.formState.errors.uniformNumber
                                ? "true"
                                : "false"
                        }
                        disabled={teamAtMaxSize}
                    />
                    {methods.formState.errors.uniformNumber &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.uniformNumber
                                .message as string,
                        })}
                </Form.Group>
                <Form.Group
                    controlId="dob"
                    as={Col}
                    xs={6}
                    lg={4}
                    className="col"
                >
                    <Form.Label>Date of Birth</Form.Label>
                    <Form.Control
                        type="text"
                        {...methods.register("dob")}
                        aria-invalid={
                            methods.formState.errors.dob ? "true" : "false"
                        }
                        disabled={teamAtMaxSize}
                    />
                    {methods.formState.errors.dob &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.dob
                                .message as string,
                        })}
                </Form.Group>
                <Form.Group
                    controlId="positions"
                    as={Col}
                    xs={6}
                    lg={4}
                    className="col"
                >
                    <Form.Label>Position(s)</Form.Label>
                    <Controller
                        name="positions"
                        control={methods.control}
                        render={({ field }) => (
                            <Select
                                {...field}
                                isMulti
                                isSearchable={false}
                                options={positions.map(p => {
                                    return {
                                        value: parseInt(p.id.toString()),
                                        label: p.position,
                                    };
                                })}
                                isDisabled={teamAtMaxSize}
                            />
                        )}
                    />
                </Form.Group>
            </Row>
            <Row>
                <Col xs={6}>
                    <Form.Group controlId="email">
                        <Form.Label>Parent Email</Form.Label>
                        <Form.Control
                            type="email"
                            {...methods.register("email")}
                            aria-invalid={
                                methods.formState.errors.email
                                    ? "true"
                                    : "false"
                            }
                            disabled={teamAtMaxSize}
                        />
                        {methods.formState.errors.email &&
                            displayFormAlert({
                                level: "danger",
                                text: methods.formState.errors.email
                                    .message as string,
                            })}
                    </Form.Group>
                </Col>
            </Row>
        </div>
    );
};

export default PlayerForm;
